import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import {
  BETA_ALLOWANCE_UNAVAILABLE_MESSAGE,
  allowancePendingDelay,
  allowancePendingShouldStop,
  isBetaWriter,
} from '../utils/betaAllowance';
import { publishAllowance } from '../utils/betaSpendPause';

/**
 * @typedef {Object} BalanceData
 * @property {number|null} balance - Remaining credits (null if unlimited)
 * @property {number} usage - Total spent
 * @property {number|null} limit - Credit limit (null if unlimited)
 * @property {boolean} is_unlimited - True if pay-as-you-go
 * @property {boolean} cached - True if returned from server cache
 * @property {string|null} error - Error message if fetch failed
 */

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const MANUAL_REFRESH_COOLDOWN_MS = 60 * 1000; // 1 minute
const QUIET_RETRY_MS = 8000;
/** A late charge should land on the action that caused it.
 * The last read is about a minute later: OpenRouter's remaining figure
 * often moves after the first two reads.
 */
export const BALANCE_CATCHUP_MS = [5000, 30000, 60000];

function hasFigure(payload) {
  return typeof payload?.balance === 'number' && Number.isFinite(payload.balance);
}

function readFailedWithoutFigure(payload) {
  if (!payload || typeof payload !== 'object') return true;
  return Boolean(payload.error) && !hasFigure(payload);
}

function provisioningOf(payload) {
  return typeof payload?.key_provisioning === 'string' ? payload.key_provisioning : null;
}

/** A new allowance state replaces whatever was on screen, even with no dollar figure. */
function stateChanged(previous, incoming) {
  const next = provisioningOf(incoming);
  if (!next) return false;
  return next !== provisioningOf(previous);
}

function betaIdentityFromError(error) {
  if (isBetaWriter(error)) return error;
  if (isBetaWriter(error?.detail)) return error.detail;
  return null;
}

export function useOpenRouterBalance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshCooldown, setIsRefreshCooldown] = useState(false);
  const lastManualRefresh = useRef(0);
  const refreshInFlight = useRef(false);
  // Latest payload, so a failed read can keep it. `data` inside fetchBalance
  // would otherwise stay null: the callback is created once.
  const dataRef = useRef(null);
  const quietRetryTimer = useRef(null);
  const quietRetryUsed = useRef(false);
  const fetchBalanceRef = useRef(null);

  const commitData = (next) => {
    dataRef.current = next;
    publishAllowance(next);
    setData(next);
    return next;
  };

  const scheduleQuietRetry = () => {
    if (quietRetryUsed.current || quietRetryTimer.current) return;
    quietRetryUsed.current = true;
    quietRetryTimer.current = setTimeout(() => {
      quietRetryTimer.current = null;
      fetchBalanceRef.current?.(false);
    }, QUIET_RETRY_MS);
  };

  const fetchBalance = useCallback(async (forceRefresh = false) => {
    // Debounce: force-refreshes can be triggered from several independent
    // places (a 402 error, the per-response completion hook in api/client.js,
    // a manual click) that can fire close together — e.g. two chat sessions
    // finishing within a second of each other. Coalesce to at most one
    // force-refresh in flight rather than firing one request per trigger.
    if (forceRefresh) {
      if (refreshInFlight.current) return dataRef.current;
      refreshInFlight.current = true;
    }
    try {
      setLoading(true);

      const endpoint = forceRefresh
        ? '/me/openrouter/balance/refresh'
        : '/me/openrouter/balance';

      // A 401 is retried once inside request(), with a fresh Clerk token.
      // Doing it again here would be a third attempt.
      const result = forceRefresh
        ? await api.post(endpoint)
        : await api.get(endpoint);

      const incoming = result?.data;
      const previous = dataRef.current;
      // Same state, no new figure: keep the number already shown. A different
      // state (beta → own, own → used up) always replaces it.
      if (previous && readFailedWithoutFigure(incoming) && !stateChanged(previous, incoming)) {
        scheduleQuietRetry();
        return previous;
      }

      if (quietRetryTimer.current) {
        clearTimeout(quietRetryTimer.current);
        quietRetryTimer.current = null;
      }
      quietRetryUsed.current = false;
      return commitData(incoming);
    } catch (error) {
      console.error('Failed to fetch OpenRouter balance:', error);
      // Don't overwrite existing cached data on transient errors.
      const previous = dataRef.current;
      if (previous) {
        scheduleQuietRetry();
        return previous;
      }
      scheduleQuietRetry();
      const beta = betaIdentityFromError(error);
      if (beta) {
        return commitData({
          balance: null,
          error: BETA_ALLOWANCE_UNAVAILABLE_MESSAGE,
          usage: 0,
          limit: null,
          is_unlimited: false,
          cached: false,
          provisioned: true,
          key_provisioning: beta.key_provisioning || 'ready',
          allowance_label: BETA_ALLOWANCE_UNAVAILABLE_MESSAGE,
        });
      }
      return commitData({
        balance: null,
        error: 'Failed to fetch balance',
        usage: 0,
        limit: null,
        is_unlimited: false,
        cached: false,
      });
    } finally {
      setLoading(false);
      if (forceRefresh) refreshInFlight.current = false;
    }
  }, []);

  fetchBalanceRef.current = fetchBalance;

  useEffect(() => () => {
    if (quietRetryTimer.current) clearTimeout(quietRetryTimer.current);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Auto-refresh every 5 minutes (only when tab is visible)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchBalance();
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchBalance]);

  // A brand-new writer's first balance read often lands while their key is
  // still being created. Keep asking, with backoff, until the allowance
  // figure is there or a while has passed. Own-key accounts are not pending,
  // so this does not run for them.
  useEffect(() => {
    if (data?.key_provisioning !== 'pending') return undefined;
    const started = Date.now();
    let cancelled = false;
    let timer = null;
    let attempt = 0;

    const schedule = () => {
      if (cancelled || allowancePendingShouldStop(Date.now() - started)) return;
      const delay = allowancePendingDelay(attempt);
      attempt += 1;
      timer = setTimeout(async () => {
        if (cancelled || allowancePendingShouldStop(Date.now() - started)) return;
        const next = await fetchBalance(false);
        if (cancelled || (next && next.key_provisioning !== 'pending')) return;
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [data?.key_provisioning, fetchBalance]);

  // Listen for balance-changed events: a 402 (insufficient credits), or a
  // completed assistant response. JSON sends dispatch from api/client.js;
  // writer-chat SSE dispatches from chat.js when the stream finishes.
  // The immediate read is followed by catch-up reads, the last about a
  // minute later, so a charge that posts after the action still shows.
  useEffect(() => {
    const timers = [];
    const clearCatchup = () => {
      while (timers.length) clearTimeout(timers.pop());
    };
    const handleForceRefresh = () => {
      fetchBalance(true);
      clearCatchup();
      BALANCE_CATCHUP_MS.forEach((delay) => {
        timers.push(setTimeout(() => fetchBalance(true), delay));
      });
    };
    window.addEventListener('openrouter:balance-changed', handleForceRefresh);
    return () => {
      window.removeEventListener('openrouter:balance-changed', handleForceRefresh);
      clearCatchup();
    };
  }, [fetchBalance]);

  // Manual refresh with cooldown
  const refresh = useCallback(async () => {
    const now = Date.now();
    if (now - lastManualRefresh.current < MANUAL_REFRESH_COOLDOWN_MS) {
      return; // Silently ignore — button is visually disabled via isRefreshCooldown
    }
    lastManualRefresh.current = now;
    setIsRefreshCooldown(true);

    await fetchBalance(true);

    // Re-enable after cooldown
    setTimeout(() => setIsRefreshCooldown(false), MANUAL_REFRESH_COOLDOWN_MS);
  }, [fetchBalance]);

  return { data, loading, refresh, isRefreshCooldown };
}
